import React, { useEffect, useState } from 'react';
import { Clock, CheckCircle, XCircle, Loader, AlertTriangle } from 'lucide-react';
import { AnalysisStatus } from '../types/survey';

interface AnalysisProgressProps {
  analysisId: string;
  onStatusUpdate: (status: AnalysisStatus) => void;
  onComplete: () => void;
  pollInterval?: number;
}

interface StepEvent {
  step: number;
  url: string | null;
  next_goal: string | null;
  // Base64 JPEG of what the agent sees this step. The hosted runtime is
  // headless, so this frame is the only way to watch the bot work. May be
  // null if vision is off or the frame couldn't be encoded.
  screenshot?: string | null;
}

const AnalysisProgress: React.FC<AnalysisProgressProps> = ({
  analysisId,
  onStatusUpdate,
  onComplete,
  pollInterval = 5000 // 5 seconds — status fallback poll interval
}) => {
  const [currentStatus, setCurrentStatus] = useState<AnalysisStatus | null>(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [steps, setSteps] = useState<StepEvent[]>([]);
  // Only the newest frame is retained. Keeping every screenshot in `steps`
  // would hold ~60 x 30KB of base64 in React state for a long run; we just
  // need the latest one to render the live view.
  const [liveFrame, setLiveFrame] = useState<string | null>(null);
  const [liveFrameStep, setLiveFrameStep] = useState<number | null>(null);

  useEffect(() => {
    const startTime = Date.now();
    let statusInterval: NodeJS.Timeout;
    let timeInterval: NodeJS.Timeout;
    let eventSource: EventSource | null = null;

    timeInterval = setInterval(() => {
      setElapsedTime(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);

    // SSE step stream — added in 0.4.5. Backend pushes per-step deltas
    // as the agent runs so the user sees a live transcript instead of
    // a 5-10 min spinner. Status polling still runs as a safety net for
    // the completion/failed transitions, but once the first SSE event
    // arrives we know the stream works and back off the poll to 30s
    // (was 5s) — saves ~110 unnecessary status fetches per 10-min run.
    let sseConnected = false;
    const backOffPolling = () => {
      if (sseConnected) return;
      sseConnected = true;
      clearInterval(statusInterval);
      statusInterval = setInterval(pollStatus, 30000);
    };

    try {
      eventSource = new EventSource(`/api/v1/survey/stream/${analysisId}`);
      eventSource.addEventListener('step', (e: MessageEvent) => {
        try {
          const payload = JSON.parse(e.data);
          if (payload?.done) {
            eventSource?.close();
            return;
          }
          backOffPolling();

          // Latest frame drives the live view. A null screenshot (vision off,
          // or an unencodable frame) leaves the previous frame on screen
          // rather than flashing to blank.
          const { screenshot, ...step } = payload as StepEvent;
          if (screenshot) {
            setLiveFrame(screenshot);
            setLiveFrameStep(step.step);
          }

          setSteps((prev) => {
            // Strip the frame before storing — see `liveFrame` above.
            const next = [...prev, step as StepEvent];
            // Keep the panel bounded so a 350-step run doesn't blow up DOM size.
            return next.length > 60 ? next.slice(next.length - 60) : next;
          });
        } catch (err) {
          console.warn('Failed to parse SSE step event:', err);
        }
      });
      eventSource.onerror = () => {
        // SSE may be blocked by a corporate proxy or CDN — close cleanly
        // and let status polling (still at 5s if no event ever arrived)
        // carry the run.
        eventSource?.close();
      };
    } catch (err) {
      console.warn('EventSource unsupported; falling back to polling only:', err);
    }

    const pollStatus = async () => {
      try {
        const response = await fetch(`/api/v1/survey/status/${analysisId}`);
        if (response.ok) {
          const status: AnalysisStatus = await response.json();
          setCurrentStatus(status);
          onStatusUpdate(status);

          if (status.status === 'completed' || status.status === 'failed') {
            clearInterval(statusInterval);
            clearInterval(timeInterval);
            eventSource?.close();
            if (status.status === 'completed') {
              onComplete();
            }
          }
        }
      } catch (error) {
        console.error('Error polling status:', error);
      }
    };

    pollStatus();
    statusInterval = setInterval(pollStatus, pollInterval);

    return () => {
      clearInterval(statusInterval);
      clearInterval(timeInterval);
      eventSource?.close();
    };
  }, [analysisId, onStatusUpdate, onComplete, pollInterval]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'queued':
        return <Clock className="w-6 h-6 text-yellow-500" />;
      case 'running':
        return <Loader className="w-6 h-6 text-blue-500 animate-spin" />;
      case 'completed':
        return <CheckCircle className="w-6 h-6 text-green-500" />;
      case 'failed':
        return <XCircle className="w-6 h-6 text-red-500" />;
      default:
        return <AlertTriangle className="w-6 h-6 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'queued':
        return 'border-yellow-200 bg-yellow-50';
      case 'running':
        return 'border-blue-200 bg-blue-50';
      case 'completed':
        return 'border-green-200 bg-green-50';
      case 'failed':
        return 'border-red-200 bg-red-50';
      default:
        return 'border-gray-200 bg-gray-50';
    }
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getProgressMessage = (status: string): string => {
    switch (status) {
      case 'queued':
        return 'Analysis queued for processing.';
      case 'running':
        return 'Analyzing survey...';
      case 'completed':
        return 'Analysis completed.';
      case 'failed':
        return 'Analysis failed. Please try again.';
      default:
        return 'Unknown status';
    }
  };

  if (!currentStatus) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-center space-x-3">
          <Loader className="w-6 h-6 animate-spin text-blue-500" />
          <span className="text-gray-600">Initializing analysis...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-lg border-2 p-6 transition-all duration-300 ${getStatusColor(currentStatus.status)}`}>
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          {getStatusIcon(currentStatus.status)}
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Analysis Status
            </h3>
            <p className="text-sm text-gray-600">
              ID: {analysisId.slice(0, 8)}...
            </p>
          </div>
        </div>
        <div className="text-right">
          <div className="text-sm text-gray-500">Elapsed Time</div>
          <div className="text-lg font-mono font-medium text-gray-900">
            {formatTime(elapsedTime)}
          </div>
        </div>
      </div>

      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">Progress</span>
          <span className="text-sm text-gray-500 capitalize">
            {currentStatus.status}
          </span>
        </div>

        {/* Progress bar */}
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all duration-500 ${
              currentStatus.status === 'queued'
                ? 'w-1/4 bg-yellow-500'
                : currentStatus.status === 'running'
                ? 'w-3/4 bg-blue-500'
                : currentStatus.status === 'completed'
                ? 'w-full bg-green-500'
                : 'w-full bg-red-500'
            }`}
          />
        </div>
      </div>

      <div className="space-y-2">
        <p className="text-gray-700 font-medium">
          {getProgressMessage(currentStatus.status)}
        </p>

        {currentStatus.message && (
          <p className="text-sm text-gray-600">
            {currentStatus.message}
          </p>
        )}

        {currentStatus.status === 'running' && (
          <div className="mt-4 p-3 bg-blue-100 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>Processing:</strong> The analysis system is navigating the survey
              and identifying bot-detection mechanisms. This may take several minutes.
            </p>
          </div>
        )}

        {currentStatus.status === 'failed' && (
          <div className="mt-4 p-3 bg-red-100 rounded-lg">
            <p className="text-sm text-red-800">
              <strong>Analysis failed.</strong> Please verify the survey URL and try again.
            </p>
          </div>
        )}
      </div>

      {/* Estimated completion time */}
      {currentStatus.status === 'running' && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex items-center justify-between text-sm text-gray-600">
            <span>Estimated completion:</span>
            <span>5-10 minutes total</span>
          </div>
        </div>
      )}

      {/* Live view — the hosted runtime is headless, so there is no browser
          window to watch. The backend streams a downscaled JPEG of each step
          over the same SSE channel; this renders the newest frame. */}
      {liveFrame && currentStatus.status === 'running' && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">
              Live view — what the bot sees
            </span>
            {liveFrameStep !== null && (
              <span className="text-xs text-gray-500">step {liveFrameStep}</span>
            )}
          </div>
          <img
            src={`data:image/jpeg;base64,${liveFrame}`}
            alt="Live view of the survey as the agent sees it"
            className="w-full rounded-md border border-gray-200 shadow-sm bg-white"
          />
        </div>
      )}

      {/* Live step transcript — added in 0.4.5. Streams via SSE from
          /api/v1/survey/stream/{id}. Each row is one browser-use step. */}
      {steps.length > 0 && currentStatus.status === 'running' && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">
              Live step transcript
            </span>
            <span className="text-xs text-gray-500">
              {steps.length} step{steps.length === 1 ? '' : 's'}
            </span>
          </div>
          <div className="max-h-48 overflow-y-auto rounded-md border border-gray-200 bg-white/60 text-xs font-mono">
            {steps.map((s, i) => (
              <div
                key={`${s.step}-${i}`}
                className="px-3 py-1.5 border-b border-gray-100 last:border-b-0"
              >
                <span className="text-gray-500">step {s.step}:</span>{' '}
                <span className="text-gray-800">
                  {s.next_goal || '(no goal)'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalysisProgress;